import milestoneRuleExecutionConfig from "../../data/milestoneRuleExecution.json";
import { runRulesMulti } from "../../services/rulesApi";

function getMilestoneContextsBySeverity(milestoneName, severity) {
    const normalizedMilestoneName = String(milestoneName || "").trim().toLowerCase();
    const mappings = Array.isArray(milestoneRuleExecutionConfig?.[severity]) ? milestoneRuleExecutionConfig[severity] : [];
    const contexts = new Set();

    mappings.forEach(mapping => {
        const mappingMilestone = String(mapping?.MilestoneName || "").trim().toLowerCase();

        if (mappingMilestone !== normalizedMilestoneName) {
            return;
        }

        (mapping?.ContextName || []).forEach(contextName => {
            const value = String(contextName || "").trim();

            if (value) {
                contexts.add(value);
            }
        });
    });

    return [...contexts];
}

function getRuleIssueMessages(result, severityLabel) {
    const issues = [];
    const groups = Array.isArray(result?.groups) ? result.groups : [];

    groups.forEach(group => {
        const groupName = group?.groupName || "Unknown Context";
        const rules = Array.isArray(group?.rules) ? group.rules : [];

        rules.forEach(rule => {
            const ruleSeverity = String(rule?.severity || "PASS").toUpperCase();

            if (ruleSeverity === "PASS") {
                return;
            }

            const ruleName = rule?.ruleName || "Unnamed rule";
            const message = rule?.message || ruleSeverity;
            issues.push(`[${severityLabel}] ${groupName} - ${ruleName}: ${message}`);
        });
    });

    return issues.slice(0, 20);
}

function normalizeRulesResult(result) {
    return {
        overallStatus: result?.overallStatus || "PASS",
        summary: {
            passCount: result?.summary?.passCount || 0,
            warningCount: result?.summary?.warningCount || 0,
            fatalCount: result?.summary?.fatalCount || 0,
            errorCount: result?.summary?.errorCount || 0
        },
        groups: Array.isArray(result?.groups) ? result.groups : []
    };
}

function mergeRulesResults(results) {
    const normalized = results.map(normalizeRulesResult);
    const mergedSummary = normalized.reduce(
        (acc, current) => {
            acc.passCount += current.summary.passCount || 0;
            acc.warningCount += current.summary.warningCount || 0;
            acc.fatalCount += current.summary.fatalCount || 0;
            acc.errorCount += current.summary.errorCount || 0;
            return acc;
        },
        { passCount: 0, warningCount: 0, fatalCount: 0, errorCount: 0 }
    );

    const mergedGroups = normalized.flatMap(item => item.groups || []);

    let overallStatus = "PASS";
    if (mergedSummary.fatalCount > 0) {
        overallStatus = "FATAL";
    } else if (mergedSummary.errorCount > 0) {
        overallStatus = "ERROR";
    } else if (mergedSummary.warningCount > 0) {
        overallStatus = "WARNING";
    }

    return {
        overallStatus,
        summary: mergedSummary,
        groups: mergedGroups
    };
}

export async function runPreMilestoneCompletionRules({ loanId, milestoneName, requestedBy }) {
    const fatalContexts = getMilestoneContextsBySeverity(milestoneName, "Fatal");
    const warningContexts = getMilestoneContextsBySeverity(milestoneName, "Warning");

    if (fatalContexts.length === 0 && warningContexts.length === 0) {
        return {
            executed: false,
            hasBlockingIssues: false,
            validationMessages: [],
            rulesResult: null
        };
    }

    const executions = [];

    if (fatalContexts.length > 0) {
        executions.push({
            label: "Fatal",
            contexts: fatalContexts,
            promise: runRulesMulti({
                loanId,
                contextName: fatalContexts,
                fields: [],
                ruleId: null,
                showFatalAsWarning: false,
                requestedBy
            })
        });
    }

    if (warningContexts.length > 0) {
        executions.push({
            label: "Warning",
            contexts: warningContexts,
            promise: runRulesMulti({
                loanId,
                contextName: warningContexts,
                fields: [],
                ruleId: null,
                showFatalAsWarning: true,
                requestedBy
            })
        });
    }

    const settledResults = await Promise.allSettled(executions.map(item => item.promise));
    const validationMessages = [];
    const successfulResults = [];
    let hasExecutionFailures = false;

    settledResults.forEach((settled, index) => {
        const execution = executions[index];
        const contextLabel = execution.contexts.join(", ");

        if (settled.status === "rejected") {
            hasExecutionFailures = true;
            validationMessages.push(
                `[${execution.label}] Rule execution failed for context(s): ${contextLabel}. ${settled.reason?.message || "Unknown error."}`
            );
            return;
        }

        const result = settled.value || {};
        successfulResults.push(result);
        const summary = result?.summary || { warningCount: 0, fatalCount: 0, errorCount: 0 };
        const hasIssues = (summary.warningCount || 0) > 0 || (summary.fatalCount || 0) > 0 || (summary.errorCount || 0) > 0;

        if (!hasIssues) {
            return;
        }

        validationMessages.push(`[${execution.label}] Rule execution returned issues for context(s): ${contextLabel}.`);
        validationMessages.push(...getRuleIssueMessages(result, execution.label));
    });

    let mergedResult = mergeRulesResults(successfulResults);

    if (successfulResults.length === 0 && validationMessages.length > 0) {
        mergedResult = {
            overallStatus: "ERROR",
            summary: { passCount: 0, warningCount: 0, fatalCount: 0, errorCount: validationMessages.length },
            groups: [],
            errorMessage: "Rules execution failed for configured context(s)."
        };
    }

    const mergedSummary = mergedResult?.summary || { warningCount: 0, fatalCount: 0, errorCount: 0 };
    const hasBlockingIssues = hasExecutionFailures || (mergedSummary.fatalCount || 0) > 0;

    if (hasBlockingIssues) {
        mergedResult = {
            ...mergedResult,
            overallStatus: "FATAL"
        };
    }

    return {
        executed: true,
        hasBlockingIssues,
        validationMessages,
        rulesResult: mergedResult
    };
}
